export interface Batch {
  id: number;
  batch_number: string;
  product_id: number;
  received_at: string;
  expiry_date: string | null;
  supplier_reference: string | null;
  blocked_at: string | null;
  block_reason: string | null;
  blocked_until: string | null;
  blocked_by_user_id: number | null;
  allow_entries_when_blocked: boolean;
  unblocked_at: string | null;
  unblocked_by_user_id: number | null;
  deleted_at: string | null;
  total_stock?: number;
  product?: BatchProduct;
  stock?: BatchStock[];
  created_at: string;
  updated_at: string;
}

export interface BatchProduct {
  id: number;
  name: string;
  code: string;
  description?: string;
  status?: string;
  cost?: string;
  image_hash: string | null;
  image_url?: string;
  team_id?: number;
  batch_active?: boolean;
  batch_type?: string | null;
}

export interface CreateBatchRequest {
  product_id: number;
  batch_number: string;
  received_at: string;
  expiry_date?: string | null;
  supplier_reference?: string | null;
}

export interface UpdateBatchRequest {
  batch_number?: string;
  received_at?: string;
  expiry_date?: string | null;
  supplier_reference?: string | null;
}

export interface BlockBatchRequest {
  reason?: string | null;
  blocked_until?: string | null;
  allow_entries?: boolean;
}

export interface BatchFilters {
  searchTerm?: string;
  status?: 'active' | 'expired' | 'depleted' | 'blocked';
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  advanced_filters?: {
    filters: Array<{
      column: string;
      operator: string;
      value: any;
    }>;
    logic: 'AND' | 'OR';
  };
}

export interface BatchesApiResponse {
  data: Batch[];
  meta: {
    currentPage: number;
    lastPage: number;
    perPage: number;
    total: number;
  };
  links: Record<string, string | null>;
}

export interface BatchPage {
  batches: Batch[];
  nextPage?: number;
  hasMore: boolean;
  total: number;
}

export interface BatchStock {
  location_id: number;
  location_code?: string;
  /** Respuesta de `/batches/{id}/stock` */
  quantity?: number;
  /** Respuesta anidada en `/products/{id}/batches` */
  stock?: number;
}

export interface BatchTraceabilityEvent {
  event_type: string;
  direction: string;
  quantity: number;
  entry_id: number | null;
  order_id: number | null;
  created_at: string;
}

export interface BatchStatusCounts {
  all: number;
  active: number;
  blocked: number;
  expired: number;
  depleted: number;
}

export interface ActivateProductBatchInput {
  batch_number: string;
  expiry_date?: string;
  location_ids?: number[];
}

export interface ActivateProductBatchesRequest {
  batch_type: string;
  batches?: ActivateProductBatchInput[];
}

export interface ActivateProductBatchLocationResult {
  location_id: number;
  location_code: string;
  quantity_assigned: number;
}

export interface ActivateProductBatchResult {
  batch_id: number;
  batch_number: string;
  locations: ActivateProductBatchLocationResult[];
  stock_rows_assigned: number;
  quantity_assigned: number;
}

export interface ActivateProductBatchesResponse {
  batches: ActivateProductBatchResult[];
  stock_rows_assigned: number;
  quantity_assigned: number;
}

export function getBatchTotalStock(batch: Pick<Batch, 'total_stock' | 'stock'>): number {
  if (batch.total_stock != null) {
    const total = Number(batch.total_stock);
    if (!Number.isNaN(total)) return total;
  }

  if (!Array.isArray(batch.stock)) return 0;

  return batch.stock.reduce((sum, entry) => {
    const qty = Number(entry.quantity ?? entry.stock);
    return sum + (Number.isNaN(qty) ? 0 : qty);
  }, 0);
}
